import { Component, inject, OnInit, signal } from "@angular/core";
import { MatExpansionModule } from "@angular/material/expansion";
import { ProductSlDetailsListComponent } from "../product-sl-details-list/product-sl-details-list.component";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { noWhitespaceValidator } from '../../../../utils/no-whitespace.validator';
import { SystemAdminService } from "../../../../services/system-admin.service";
import { CommonModule } from "@angular/common";
import { ProductApprovedListResponse, ProductSlDetailsRequestPayload, ProductStateApprovedListResponse } from "../../../../models/SystemAdmin.model";
import { AuthService } from "../../../../auth/auth.service";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, map } from "rxjs";
import { StateList } from "../../../../models/RequestQuote";
import { PropertyService } from "../../../../services/property.service";
import { ToastService } from "../../../../services/toast.service";
import { ProductSlRemoveType, ToastType } from "../../../../enums/config.enum";
import { MatSelectModule } from "@angular/material/select";

@Component({
    selector: 'app-product-details',
    standalone: true,
    imports: [CommonModule, MatExpansionModule, ProductSlDetailsListComponent, ReactiveFormsModule, MatSelectModule],
    templateUrl: './product-details.component.html',
    styleUrl: './product-details.component.scss'
})
export class ProductDetails implements OnInit{
    userId = signal<string>('');
    router = inject(Router);
    approvedProductList = signal<ProductApprovedListResponse[]>([]);
    approvedStatesList = signal<StateList[]>([]);
    searchApprovedStatesList = signal<any>('');
    filteredApprovedStatesList = signal<StateList[]>([]); 
    activateRoute = inject(ActivatedRoute);
    authService = inject(AuthService);
    propertyService = inject(PropertyService);
    systemAdminService = inject(SystemAdminService);
    toastService = inject(ToastService);
    fb: FormBuilder = inject(FormBuilder);
    productSlDetailsList = signal<any[]>([]);
    productSLDetailsForm = this.fb.group({
        productId: [null, [Validators.required, noWhitespaceValidator]],
        stateId: [null, [Validators.required, noWhitespaceValidator]],
        personalLicenseHeld: [1, [Validators.required, noWhitespaceValidator]],
        slLicenseNumber: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^[a-zA-Z0-9\\s\\-\\.]+$'), Validators.maxLength(128)]],
        slLicenseExpiryDate: [null, [Validators.required, noWhitespaceValidator, Validators.pattern('^(19|20)\\d\\d-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$')]],
        isActive: [1]
    });

    ngOnInit(): void {
      this.activateRoute.params.subscribe({
          next: (params) => {
            if(params && params['userId']){
              this.userId.set(params['userId']);
              this.getApprovedProductAndStateList(params['userId']);
            }
          }
      }); 

      // Watch for changes in lloydsBrokerAssigned
      this.productSLDetailsForm.get('personalLicenseHeld')?.valueChanges.subscribe(value => {
        this.toggleFields(value === 1);  // Enable/Disable fields based on value
      });

      // Initially update the form based on the default value
      this.toggleFields(this.productSLDetailsForm.get('personalLicenseHeld')?.value === 1);
    }

    // Enable or disable fields based on the personalLicenseHeld value
    toggleFields(enable: boolean): void {
      const fields = ['slLicenseNumber', 'slLicenseExpiryDate'];
  
      if (enable) {
        // Enable and make fields required
        fields.forEach(field => {
            const control = this.productSLDetailsForm.get(field);
            control?.enable();
            // Add the 'required' validator without removing existing validators
            const existingValidators = control?.validator ? [control?.validator] : [];
            control?.setValidators([Validators.required, noWhitespaceValidator, ...existingValidators]);

            control?.updateValueAndValidity(); // Revalidate the form control
        });
      } else {
        // Disable fields and remove required validator
        fields.forEach(field => {
            const control = this.productSLDetailsForm.get(field);
            control?.reset();  // Reset the value to null or default value
            control?.disable();
            // Get current validators
            const currentValidators = control?.validator ? [control?.validator] : [];
            // Remove the `required` validator
            const filteredValidators = currentValidators.filter(validator => validator !== Validators.required && validator !== noWhitespaceValidator);
            // Set the remaining validators (other than required)
            control?.setValidators(filteredValidators);
            // Update validity
            control?.updateValueAndValidity();
        });
      }
    }

    getApprovedProductAndStateList(userId: string) {
        forkJoin([
          this.systemAdminService.getAllApprovedProductList(),
          this.propertyService.getStates(),
        ]).pipe(
          map(([approvedProducts, approvedStates]) => {
              return {approvedProducts, approvedStates};
          })
        ).subscribe({
          next: (response: any) => {
            if(response){
              this.approvedProductList.set(response.approvedProducts);
              this.approvedStatesList.set(response.approvedStates);
              this.filteredApprovedStatesList.set(response.approvedStates);
              this.getAllApprovedProductSLList(userId);
            }
          },
          error: (error: any) => {
            console.log("error", error);
            this.toastService.addNotification({message: 'System error. Failed to retrieve data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
      })
    }

    getProdutName(productId: any) {
      const filteredData =  this.approvedProductList().filter((product) => product.productId === productId);
      if(filteredData.length > 0) {
        return filteredData[0]?.productName;
      }
      return null;
    }

    getStateName(stateId: any) {
      const filteredData =  this.approvedStatesList().filter((state) => state.stateId === stateId);
      if(filteredData.length > 0) {
        return filteredData[0]?.stateName;
      }
      return null;
    }

    getApprovedStateName(stateId: any): string {
      if (!stateId) return '';
      const state =Array.from(this.approvedStatesList().values()).flat().find(s => String(s.stateId) === String(stateId));
      return state ? state.stateName : '';
    }

    onApprovedStateDropdownOpened(isOpen: boolean){
      if (!isOpen) {
        this.searchApprovedStatesList.set('');
        this.filteredApprovedStatesList.set([...this.approvedStatesList()]);
      }
    }

    onApprovedStateSearch(event: Event) {
      const value = (event.target as HTMLInputElement).value; 
  
      this.propertyService.applyDelayedFilter("ApprovedPrdState", 1200, () => {
        this.updateApprovedStateSearchTerm(value);
      });
    }
    
    updateApprovedStateSearchTerm(value: string) {
      //const value = event.target ? (event.target as HTMLInputElement).value : '';
      this.searchApprovedStatesList.set(value);
      const filtered = this.approvedStatesList().filter((state) =>
          state.stateName.toLowerCase().includes(value.toLowerCase())
      );
      this.filteredApprovedStatesList.set(filtered);
    }

    getAllApprovedProductSLList(userId: string) {
      this.systemAdminService.getAllApprovedProductStatesList(userId).subscribe({
        next:(response: ProductStateApprovedListResponse[]) => {
          if(response){
            if(response.length > 0) {
              const updatedResponse = response.filter(item => item.isActive === 1).map((item) => ({
                ...item,
                personalLicenseHeld: item?.personalLicenseHeld ? 'Yes' : 'No',
                approvedProducts: item?.productId ? this.getProdutName(item?.productId) : null,
                stateId: item?.productId ? this.getStateName(item?.stateId) : null
              }));
              this.productSlDetailsList.set(updatedResponse); 
            }
          }
        },
        error: (error: any) => {
          console.log("error", error);
        }
      })
    }

    resetProductSlDetails() {
      this.productSLDetailsForm.patchValue({productId: null,stateId: null,personalLicenseHeld: 1, slLicenseNumber: null, slLicenseExpiryDate: null, isActive: 1});
      this.productSLDetailsForm?.markAsUntouched();
    }

    saveProductSlDetails(payload: ProductSlDetailsRequestPayload) {
      if(payload.personalLicenseHeld === 0){
        delete payload.slLicenseNumber;
        delete payload.slLicenseExpiryDate;
      }
      this.systemAdminService.saveProductSlDetails(payload).subscribe({
          next:(response: any) => {
            if(response){
              // if response is success then prompt and get updated Details
              const msg =  response?.message || "User approved product state created successfully.";
              this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
              this.resetProductSlDetails() // reset the form
              this.getAllApprovedProductSLList(this.userId());
            }
          },
          error: (error: any) => {
            console.log("error", error);
            this.toastService.addNotification({message: 'System error. Failed to save data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
      });
    }

    removeProductSlDetail(approvedId: number) {
      this.systemAdminService.removeProductSlDetails(approvedId).subscribe({
          next: (response: any) => {
              if(response){
                // if response is success then prompt and get updated Details
                const msg =  "User Approved States updated successfully.";
                this.toastService.addNotification({message: msg, type: ToastType.Success}); // success notification
                this.getAllApprovedProductSLList(this.userId());
              }
          },
          error: (error: any) => {
            console.log("error", error);
            this.toastService.addNotification({message: 'System error. Failed to save data. Please exit and contact help@AbacusAU.com.', type: ToastType.Error});
          }
      })
    }

    onAddAction() {
        if(this.productSLDetailsForm.invalid){
            return;
        }

        const {productId, stateId, personalLicenseHeld, slLicenseNumber, slLicenseExpiryDate, isActive} = this.productSLDetailsForm.getRawValue();
        const payload: ProductSlDetailsRequestPayload = {
            userId: Number(this.userId()), productId, stateId, personalLicenseHeld, slLicenseNumber, slLicenseExpiryDate, isActive,
            createdUserId: Number(this.authService.getUserDetailsFromStorage()?.createdUserId), updatedUserId: Number(this.authService.getUserDetailsFromStorage()?.updatedUserId)
        }
        this.saveProductSlDetails(payload);
    }
}